import { Directive, ElementRef, OnDestroy, OnInit } from '@angular/core';

/**
 * Directive that highlights MathJax-rendered equations (mjx-container elements)
 * when they fall within the user's text selection.
 *
 * The browser's native ::selection pseudo-element does not apply to SVG content
 * inside mjx-container, so this directive listens to the document selectionchange
 * event and toggles a 'math-selected' CSS class on each mjx-container that
 * intersects the current selection range.
 *
 * Example usage:
 *   <div sabisHighlightMathSelection #messageContent ...></div>
 */
@Directive({
  selector: '[sabisHighlightMathSelection]',
  standalone: true,
})
export class HighlightMathSelectionDirective implements OnInit, OnDestroy {
  private _selectionChangeHandler = () => this._highlightMathInSelection();

  constructor(private _el: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    document.addEventListener('selectionchange', this._selectionChangeHandler);
  }

  ngOnDestroy(): void {
    document.removeEventListener('selectionchange', this._selectionChangeHandler);
  }

  private _highlightMathInSelection(): void {
    const containers = this._el.nativeElement.querySelectorAll<HTMLElement>('mjx-container');
    if (!containers.length) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      containers.forEach(c => c.classList.remove('math-selected'));
      return;
    }

    const range = selection.getRangeAt(0);
    containers.forEach(container => {
      if (range.intersectsNode(container)) {
        container.classList.add('math-selected');
      } else {
        container.classList.remove('math-selected');
      }
    });
  }
}
